import React from 'react';
import { useSetAtom } from "jotai";
import { FormatTextProps } from "../types/lib/formatText";
import { imgUrlAtom } from "../atoms";

// URL、電話番号、画像URL判定用の正規表現
export const urlRegex = /https?:\/\/[-_.!~*'()（）A-Za-z0-9;/?:@&=+$,%#\u3000-\u30ff\u3040-\u309f\u30e0-\u9fcf]+/gi;
const telRegex = /(?:(?:\(0\d{1,4}\)|（0\d{1,4}）|0\d{1,4})-?\d{1,4}-\d{4})/g;
export const imageRegex = /(jpeg|jpg|gif|png)/i;


const markdownPatterns = [
  /\[(.*?)\]\((.*?)\)/, // リンク
  // /(^|\s)(#{1,6}\s)/, // 見出し
  // /(\*\*|__)(.*?)\1/, // 強調
  // /(^|\s)([-*+]\s)/, // リスト
  // /!\[(.*?)\]\((.*?)\)/, // 画像
  // /(^|\s)(\d+\.\s)/ // 数字のリスト
];

/**
 * processLinks
 *
 * 1 行分のテキストに対して、以下の処理を行います。
 * - 数値の場合、カンマ区切りの変換（showThousandSeparator が true の場合）
 * - URL、電話番号、画像URLを検出して、それぞれリンクや画像に変換
 *
 * @param text 1 行分のテキスト
 * @param onClickImg 画像クリック時のハンドラ
 * @param showThousandSeparator 数値のカンマ区切りを有効にするかどうか
 * @returns React ノードの配列
 */
const processLinks = (
  text: string,
  onClickImg: (path: string) => void,
  showThousandSeparator: boolean
): React.ReactNode[] => {
  // URL と電話番号の両方にマッチする正規表現を生成
  const combinedRegex = new RegExp(`${urlRegex.source}|${telRegex.source}`, 'gi');
  
  // マッチした部分でテキストを分割
  const segments = text.split(combinedRegex);
  const matches = text.match(combinedRegex);
  const nodes: React.ReactNode[] = [];

  segments.forEach((segment, index) => {
    // 通常テキスト部分
    if (segment) {
      const formattedSegment =
        showThousandSeparator && !isNaN(Number(segment))
          ? segment.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
          : segment;
      nodes.push(formattedSegment);
    }
    // 分割位置に対応するマッチがあれば処理
    if (matches && index < matches.length) {
      const match = matches[index];

      // 電話番号の場合
      if (new RegExp(telRegex.source).test(match)) {
        const tel = match.replace(/[()（）]/g, '');
        nodes.push(
          <a key={`tel-${index}`} href={`tel:${tel}`}>
            {match}
          </a>
        );
      }
      // 画像URLの場合
      else if (imageRegex.test(match)) {
        nodes.push(
          <img
            key={`img-${index}`}
            src={match}
            alt="image"
            style={{ maxWidth: '100%', height: 'auto' }}
            onClick={() => onClickImg(match)}
          />
        );
      }
      // 通常のURLの場合
      else if (new RegExp(urlRegex.source, 'i').test(match)) {
        nodes.push(
          <a key={`url-${index}`} href={match} target="_blank" rel="noopener noreferrer">
            {match}
          </a>
        );
      }
      // 該当しない場合はそのままテキストとして追加
      else {
        nodes.push(match);
      }
    }
  });

  return nodes;
};

/**
 * processNewlines
 *
 * テキスト全体を改行コードで分割し、各行ごとに processLinks を適用してから、
 * 行と行の間に <br /> タグを挿入して結合します。
 *
 * @param text 対象のテキスト全体
 * @param onClickImg 画像クリック時のハンドラ
 * @param showThousandSeparator 数値のカンマ区切りを有効にするかどうか
 * @returns 改行とリンク変換を適用した React ノード
 */
const processNewlines = (
  text: string,
  onClickImg: (path: string) => void,
  showThousandSeparator: boolean
): React.ReactNode => {
  // 改行コードでテキストを分割
  const lines = text.split(/\r\n|\n|\r/);
  const content: React.ReactNode[] = [];

  lines.forEach((line, idx) => {
    content.push(
      <React.Fragment key={`line-${idx}`}>
        {
          // マークダウンかどうかを確認
          markdownPatterns.some(pattern => pattern.test(line)) ? 
            processMarkdown(line)
            :
            processLinks(line, onClickImg, showThousandSeparator)
        }
      </React.Fragment>
    );
    // 最終行以外には改行タグを挿入
    if (idx < lines.length - 1) {
      content.push(<br key={`br-${idx}`} />);
    }
  });

  return content;
};


/**
 * リンクのマークダウンを<a>タグに変換する関数
 */
const formatedMarkdownLink = (text: string): React.ReactNode => {
  const linkPattern = /\[(.*?)\]\((.*?)\)/g;
  const parts = [];
  let lastIndex = 0;

  text.replace(linkPattern, (match, p1, p2, offset) => {
    parts.push(text.slice(lastIndex, offset));
    parts.push(<a href={p2} key={offset} target="_blank" rel="noopener noreferrer">{p1}</a>);
    lastIndex = offset + match.length;
    return match;
  });

  parts.push(text.slice(lastIndex));
  return parts;
};


/**
 * マークダウン表記の変換
 */
const processMarkdown = (text: string): React.ReactNode => {
  if(markdownPatterns[0].test(text)) return formatedMarkdownLink(text);
  return text;
};


/**
 * FormatText コンポーネント
 *
 * 入力テキストに対して、改行およびリンク変換処理を適用してレンダリングします。
 */
export const FormatText: React.FC<FormatTextProps> = ({ text, showThousandSeparator }) => {
  const setImgUrl = useSetAtom(imgUrlAtom);

  if (typeof text !== 'string') {
    return <>{text}</>;
  }
  if (!text) {
    return <></>;
  }

  // 画像クリック時の処理（例：画像拡大表示など）
  const onClickImg = (path: string) => {
    setImgUrl(path);
  };

  return processNewlines(text, onClickImg, showThousandSeparator ?? false);
};
